import {debounce} from 'lodash';
import {useCallback, useEffect, useRef} from 'react';

import {mediaFullscreenModalImageScroll} from '../../../services/analytics';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {type ViewportCenterPercent} from './useScrollCenterPan';

/**
 * Debounced analytics for user-initiated scrolls in the fullscreen modal,
 * reporting the viewport center where the scroll started and where it ended.
 * The caller is responsible for filtering out programmatic scrolls.
 */
export function useFullscreenScrollAnalytics(
  viewportCenterPercent: ViewportCenterPercent
) {
  // Ref to track the viewport center when the scroll started
  const scrollStartViewportCenterRef = useRef<ViewportCenterPercent | null>(
    null
  );
  const debouncedLogScrollRef = useRef(
    debounce(
      // oxlint-disable-next-line react/refs
      (eventData: {
        scrollLeft: number;
        scrollTop: number;
        prevViewportCenterPercentX: number;
        prevViewportCenterPercentY: number;
        newViewportCenterPercentX: number;
        newViewportCenterPercentY: number;
      }) => {
        recordRumAction('full_screen_modal_image_scroll', eventData);
        mediaFullscreenModalImageScroll(eventData);
        scrollStartViewportCenterRef.current = null;
      },
      300
    )
  );
  useEffect(() => () => debouncedLogScrollRef.current.cancel(), []);

  return useCallback(
    (el: HTMLDivElement, next: ViewportCenterPercent) => {
      if (scrollStartViewportCenterRef.current === null) {
        scrollStartViewportCenterRef.current = {
          x: viewportCenterPercent.x,
          y: viewportCenterPercent.y,
        };
      }
      debouncedLogScrollRef.current({
        scrollLeft: el.scrollLeft,
        scrollTop: el.scrollTop,
        prevViewportCenterPercentX: scrollStartViewportCenterRef.current.x,
        prevViewportCenterPercentY: scrollStartViewportCenterRef.current.y,
        newViewportCenterPercentX: next.x,
        newViewportCenterPercentY: next.y,
      });
    },
    [viewportCenterPercent]
  );
}
